import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackLink } from "@/components/BackLink";
import { ReportButton } from "@/components/ReportButton";
import { Users, Plus, Mail, Check, X, Crown, UserMinus, Trash2, Briefcase, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// 18-B2 item 7: person-hours / actions / distinct causes, scoped to the team.
function TeamImpactStrip({ team }) {
  const [impact, setImpact] = useState(null);
  useEffect(() => { api.teamImpact(team.id).then(setImpact).catch(() => {}); }, [team.id]);
  if (!impact) return null;
  return (
    <div className="bg-cream/70 border border-forest/10 rounded-xl px-4 py-3 flex items-center gap-5 flex-wrap" data-testid={`team-impact-${team.id}`}>
      <div className="text-sm text-forest"><b>{impact.person_hours}</b> person-hours</div>
      <div className="text-sm text-forest"><b>{impact.actions}</b> actions</div>
      <div className="text-sm text-forest"><b>{impact.distinct_causes}</b> causes</div>
      <Link
        to={`/teams/${team.id}/quarterly`}
        data-testid={`team-quarterly-link-${team.id}`}
        className="ml-auto inline-flex items-center gap-1.5 text-xs text-forest underline underline-offset-2"
      >
        <Printer className="w-3 h-3" /> Quarterly summary
      </Link>
    </div>
  );
}

export default function Teams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [name, setName] = useState("");
  const [teamType, setTeamType] = useState("friends"); // 18-B2 item 7
  const [inviteEmail, setInviteEmail] = useState({});

  const load = () =>
    api.myTeams().then((d) => { setTeams(d.teams || []); setInvites(d.invites || []); }).catch(() => {});

  useEffect(() => { load(); }, []);

  const isLeader = (t) => (t.leader_user_ids || [t.owner_user_id]).includes(user?.user_id);

  const create = async () => {
    if (!name.trim()) return;
    await api.createTeam(name.trim(), teamType);
    setName("");
    toast.success(teamType === "employer"
      ? "Employer team requested — our team will approve it shortly"
      : "Team created — you're the team leader");
    load();
  };

  const invite = async (teamId) => {
    const email = (inviteEmail[teamId] || "").trim();
    if (!email) return;
    try {
      const res = await api.inviteToTeam(teamId, email);
      setInviteEmail((s) => ({ ...s, [teamId]: "" }));
      toast.success(res.already_invited ? "Already invited" : res.found_user ? "Invite sent" : "Invite saved (user not on CivicHearth yet)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    }
  };

  const respond = async (teamId, accept) => {
    await api.respondTeamInvite(teamId, accept);
    toast[accept ? "success" : "message"](accept ? "Joined team" : "Invite declined");
    load();
  };

  const promote = async (teamId, m) => {
    try {
      await api.promoteMember(teamId, m.user_id);
      toast.success(`${m.name} is now a team leader`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not promote"); }
  };

  const remove = async (teamId, m) => {
    try {
      await api.removeMember(teamId, m.user_id);
      toast(`${m.name} removed from the team`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove member"); }
  };

  const destroy = async (t) => {
    try {
      await api.deleteTeam(t.id);
      toast(`${t.name} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete team"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="teams-page">
      <Seo title="Teams" />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Volunteer together</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight flex items-center gap-2">
        <Users className="w-7 h-7 text-forest" /> Your teams
      </h1>
      <p className="text-neutral-600 text-sm mt-2">Create a team, invite friends, then apply to opportunities together.</p>

      <Card className="mt-6 border-neutral-200">
        <CardContent className="p-6">
          <div className="flex gap-2 flex-wrap">
            <Input
              data-testid="team-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Team name (e.g. Weekend Warriors)"
              className="max-w-sm"
            />
            <Button data-testid="create-team-btn" onClick={create} className="rounded-full bg-forest hover:bg-forest-hover">
              <Plus className="w-4 h-4 mr-1.5" /> Create team
            </Button>
          </div>
          {/* 18-B2 item 7: employer teams queue for admin approval */}
          <div className="flex gap-4 mt-3 text-sm" data-testid="team-type-choice">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="team-type" data-testid="team-type-friends" checked={teamType === "friends"} onChange={() => setTeamType("friends")} className="accent-forest" />
              Friends &amp; neighbours
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="team-type" data-testid="team-type-employer" checked={teamType === "employer"} onChange={() => setTeamType("employer")} className="accent-forest" />
              <Briefcase className="w-3.5 h-3.5 text-forest/60" /> Employer team (admin-approved)
            </label>
          </div>
        </CardContent>
      </Card>

      {invites.length > 0 && (
        <div className="mt-8" data-testid="team-invites">
          <h2 className="font-display text-2xl text-forest tracking-tight">Invitations</h2>
          <div className="mt-4 space-y-3">
            {invites.map((inv) => (
              <div key={inv.id} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`invite-${inv.team_id}`}>
                <div>
                  <div className="font-medium text-forest">{inv.team_name}</div>
                  <div className="text-xs text-neutral-500">Invited by {inv.inviter_name}</div>
                </div>
                <div className="flex gap-2 items-center">
                  <Button data-testid={`accept-invite-${inv.team_id}`} onClick={() => respond(inv.team_id, true)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                    <Check className="w-4 h-4 mr-1.5" /> Accept
                  </Button>
                  <Button data-testid={`decline-invite-${inv.team_id}`} onClick={() => respond(inv.team_id, false)} variant="outline" className="rounded-full h-9 px-4 text-sm">
                    <X className="w-4 h-4 mr-1.5" /> Decline
                  </Button>
                  <ReportButton targetType="team" targetId={inv.team_id} label="" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {teams.length === 0 && <div className="text-sm text-neutral-500">No teams yet. Create one above.</div>}
        {teams.map((t) => {
          const lead = isLeader(t);
          return (
            <Card key={t.id} className="border-neutral-200" data-testid={`team-${t.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-display text-xl text-forest flex items-center gap-2 flex-wrap">
                    {t.name}
                    {t.team_type === "employer" && (
                      <Badge data-testid={`team-employer-badge-${t.id}`} className="rounded-full text-[10px] bg-sky-100 text-sky-800 border-none"><Briefcase className="w-3 h-3 mr-1" /> Employer</Badge>
                    )}
                    {t.team_type === "employer" && t.status === "pending" && (
                      <Badge data-testid={`team-pending-badge-${t.id}`} variant="outline" className="rounded-full text-[10px] border-amber-300 text-amber-700">Awaiting approval</Badge>
                    )}
                    {t.team_type === "employer" && t.status === "rejected" && (
                      <Badge variant="outline" className="rounded-full text-[10px] border-red-300 text-red-600">Not approved</Badge>
                    )}
                    {lead && <Badge className="rounded-full text-[10px] bg-terracotta/10 text-terracotta border-none"><Crown className="w-3 h-3 mr-1" /> You're a leader</Badge>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-neutral-500">{t.members.length} member{t.members.length !== 1 ? "s" : ""}</div>
                    {t.owner_user_id !== user?.user_id && (
                      <ReportButton targetType="team" targetId={t.id} label="" />
                    )}
                    {lead && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-team-${t.id}`} className="text-neutral-400 hover:text-red-500" aria-label="Delete team">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid={`delete-team-dialog-${t.id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {t.name}?</AlertDialogTitle>
                            <AlertDialogDescription>All members will be notified. This can't be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep team</AlertDialogCancel>
                            <AlertDialogAction data-testid={`delete-team-yes-${t.id}`} onClick={() => destroy(t)} className="bg-red-600 hover:bg-red-700">Delete team</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  {/* 18-B2 item 7: aggregate impact strip + quarterly print */}
                  {t.team_type === "employer" && t.status === "active" && <TeamImpactStrip team={t} />}
                  {t.members.map((m) => (
                    <div key={m.user_id} data-testid={`member-${t.id}-${m.user_id}`} className="flex items-center gap-2 py-1">
                      <Avatar className="w-8 h-8 border border-forest/10">
                        <AvatarImage src={m.picture} alt={m.name} />
                        <AvatarFallback className="bg-forest text-white text-xs">{m.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-neutral-700">{m.name}</span>
                      {m.is_leader && (
                        <Badge variant="outline" className="rounded-full text-[10px] border-terracotta/30 text-terracotta"><Crown className="w-3 h-3 mr-0.5" /> Leader</Badge>
                      )}
                      {lead && m.user_id !== user?.user_id && (
                        <div className="ml-auto flex gap-1.5">
                          {!m.is_leader && (
                            <button data-testid={`promote-${t.id}-${m.user_id}`} onClick={() => promote(t.id, m)}
                              className="text-xs px-2.5 py-1 rounded-full border border-neutral-200 text-neutral-600 hover:border-terracotta hover:text-terracotta inline-flex items-center gap-1 transition-colors">
                              <Crown className="w-3 h-3" /> Make leader
                            </button>
                          )}
                          <button data-testid={`remove-${t.id}-${m.user_id}`} onClick={() => remove(t.id, m)}
                            className="text-xs px-2.5 py-1 rounded-full border border-neutral-200 text-neutral-600 hover:border-red-300 hover:text-red-600 inline-flex items-center gap-1 transition-colors">
                            <UserMinus className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {t.pending_invites?.length > 0 && (
                  <div className="text-xs text-neutral-500 mt-3 inline-flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> Pending: {t.pending_invites.map((i) => i.email).join(", ")}
                  </div>
                )}
                {lead && (
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <Input
                      data-testid={`invite-email-${t.id}`}
                      value={inviteEmail[t.id] || ""}
                      onChange={(e) => setInviteEmail((s) => ({ ...s, [t.id]: e.target.value }))}
                      placeholder="Invite by email"
                      className="max-w-xs h-9"
                    />
                    <Button data-testid={`invite-btn-${t.id}`} onClick={() => invite(t.id)} variant="outline" className="rounded-full h-9 px-4 text-sm">
                      <Mail className="w-4 h-4 mr-1.5" /> Invite
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
